import React, { useContext, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import analytics from 'client/analytics';
import log from 'client/lib/log';
import ReactAppContext from 'client/core/ReactAppContext';
import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';

import useIsMounted from 'client/hooks/useIsMounted';

/**
 * Changes the page title HTML tag.
 *
 * @param {string} title The new title to set.
 */
function setPageTitle(title) {
  global.document.querySelector('head title').innerHTML = title;
}

export default function withTransitionHook(ComponentToDecorate) {
  function DecoratedComponent(props) {
    const reactAppContext = useContext(ReactAppContext);
    const { tree, apiClient, utilityApiClient } = reactAppContext;
    const isMounted = useIsMounted();
    const [isTransitioning, setIsTransitioning] = useState(true);
    const params = useParams();
    const location = useLocation();

    useEffect(() => {
      setIsTransitioning(true);
      tree.set('isTransitioning', true);

      Promise.resolve()
        .then(() => {
          if (ComponentToDecorate.fetchData) {
            return ComponentToDecorate.fetchData(
              { params },
              {
                state: tree,
                apiClient: apiClient,
                utilityApiClient: utilityApiClient,
                location: {
                  search: location.search,
                },
              },
            );
          }
        })
        .then(() => {
          if (!isMounted()) {
            return;
          }
          if (ComponentToDecorate.getPageMetadata) {
            const pageMetadata = ComponentToDecorate.getPageMetadata(tree, { params });
            if (typeof pageMetadata === 'object') {
              tree.set('pageMetadata', pageMetadata);
              tree.set('pendingCommit', true);
            }
          }
          const pageTitle = tree.get(['pageMetadata', 'pageTitle']) || 'Sunroom Rentals';
          setPageTitle(pageTitle);

          tree.set('isTransitioning', false);

          // Handle pending state commits.
          if (tree.get('pendingCommit')) {
            tree.set('pendingCommit', false);
            tree.commit();
          }

          setIsTransitioning(false);
          analytics.pageview();
        })
        .catch((error) => {
          log.error(error);
          tree.set('isTransitioning', false);
          tree.commit();
          setIsTransitioning(false);
        });

      return () => {
        setIsTransitioning(false);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return <ComponentToDecorate {...props} isTransitioning={isTransitioning} />;
  }

  configureDecoratedComponent({
    DecoratedComponent: DecoratedComponent,
    OriginalComponent: ComponentToDecorate,
    displayNameSuffix: 'WithTransitionHook',
  });

  return DecoratedComponent;
}
